/**
 * Meta Progression System - Persistent upgrades and unlocks between runs
 */
export class MetaProgression {
    constructor() {
        this.data = this.loadProgress();
        this.achievements = new Map();
        this.initializeAchievements();
    }
    
    // Save/Load system
    saveProgress() {
        try {
            localStorage.setItem('ascii_survivors_progress', JSON.stringify(this.data));
        } catch (error) {
            console.warn('Failed to save progress:', error);
        }
    }
    
    loadProgress() {
        try {
            const saved = localStorage.getItem('ascii_survivors_progress');
            if (saved) {
                return { ...this.getDefaultData(), ...JSON.parse(saved) };
            }
        } catch (error) {
            console.warn('Failed to load progress:', error);
        }
        return this.getDefaultData();
    }
    
    getDefaultData() {
        return {
            // Currency
            dataFragments: 0,
            totalDataFragments: 0,
            
            // Character unlocks
            unlockedCharacters: ['original'],
            selectedCharacter: 'original',
            
            // Permanent upgrades
            permanentUpgrades: {
                healthBoost: 0,      // +10 health per level (max 5)
                speedBoost: 0,       // +5% speed per level (max 5)
                damageBoost: 0,      // +5% damage per level (max 5)
                expBoost: 0,         // +10% exp per level (max 5)
                magnetRange: 0,      // +20% pickup range per level (max 3)
                startingWeapon: 0,   // Unlock starting weapons (max 3)
                revival: 0,          // One-time revival per run (max 1)
            },
            
            // Statistics
            stats: {
                totalRuns: 0,
                totalKills: 0,
                totalSurvivalTime: 0,
                bestSurvivalTime: 0,
                totalLevelsGained: 0,
                characterKills: {},
                achievementsUnlocked: []
            }
        };
    }
    
    // Character definitions
    getCharacterData() {
        return {
            original: {
                name: 'The Original',
                description: 'Balanced debugger with quick learning',
                unlocked: true,
                cost: 0,
                stats: {
                    healthMod: 1.0,
                    speedMod: 1.0,
                    damageMod: 1.0,
                    expMod: 1.1
                },
                startingWeapon: 'ASCII_BEAM',
                ability: 'Quick Study: +10% experience gain'
            },
            speedrunner: {
                name: 'The Speedrunner',
                description: 'Fast-moving glass cannon',
                unlocked: false,
                cost: 2000,
                unlockCondition: 'Survive 15 minutes in a single run',
                stats: {
                    healthMod: 0.7,
                    speedMod: 1.4,
                    damageMod: 1.2,
                    expMod: 1.0
                },
                startingWeapon: 'RAPID_FIRE',
                ability: 'Overclock: Movement speed doesn\'t decrease while firing'
            },
            guardian: {
                name: 'The Guardian',
                description: 'Tanky defender with area control',
                unlocked: false,
                cost: 1500,
                unlockCondition: 'Take 1000 damage in a single run',
                stats: {
                    healthMod: 1.5,
                    speedMod: 0.8,
                    damageMod: 0.9,
                    expMod: 1.0
                },
                startingWeapon: 'ASCII_BEAM',
                ability: 'Fortified: 25% damage reduction'
            },
            architect: {
                name: 'The Architect',
                description: 'Area damage specialist',
                unlocked: false,
                cost: 3000,
                unlockCondition: 'Kill 50 enemies with explosion damage',
                stats: {
                    healthMod: 1.0,
                    speedMod: 1.0,
                    damageMod: 1.3,
                    expMod: 1.0
                },
                startingWeapon: 'ROCKET',
                ability: 'Cascade: Explosions trigger additional smaller explosions'
            },
            hacker: {
                name: 'The Hacker',
                description: 'Penetration specialist with unique weapons',
                unlocked: false,
                cost: 2500,
                unlockCondition: 'Kill 100 enemies with piercing weapons',
                stats: {
                    healthMod: 0.9,
                    speedMod: 1.1,
                    damageMod: 1.1,
                    expMod: 1.0
                },
                startingWeapon: 'PIERCING_SHOT',
                ability: 'Deep Strike: All weapons gain +1 piercing'
            }
        };
    }
    
    initializeAchievements() {
        const achievements = [
            {
                id: 'first_run',
                name: 'System Boot',
                description: 'Complete your first debug session',
                reward: 100,
                condition: (stats) => stats.totalRuns >= 1
            },
            {
                id: 'survivor_5',
                name: 'Runtime Stability',
                description: 'Survive for 5 minutes',
                reward: 200,
                condition: (stats) => stats.bestSurvivalTime >= 300
            },
            {
                id: 'survivor_10',
                name: 'Extended Uptime',
                description: 'Survive for 10 minutes',
                reward: 300,
                condition: (stats) => stats.bestSurvivalTime >= 600
            },
            {
                id: 'survivor_15',
                name: 'Marathon Process',
                description: 'Survive for 15 minutes',
                reward: 500,
                condition: (stats) => stats.bestSurvivalTime >= 900
            },
            {
                id: 'killer_100',
                name: 'Threat Eliminator',
                description: 'Eliminate 100 corrupted entities',
                reward: 150,
                condition: (stats) => stats.totalKills >= 100
            },
            {
                id: 'killer_500',
                name: 'System Cleaner',
                description: 'Eliminate 500 corrupted entities',
                reward: 300,
                condition: (stats) => stats.totalKills >= 500
            },
            {
                id: 'level_10',
                name: 'Memory Optimizer',
                description: 'Reach level 10 in a single run',
                reward: 250,
                condition: (stats, session) => session && session.level >= 10
            }
        ];
        
        achievements.forEach(achievement => {
            this.achievements.set(achievement.id, achievement);
        });
    }
    
    // Character management
    canUnlockCharacter(characterId) {
        const character = this.getCharacterData()[characterId];
        if (!character || character.unlocked) return false;
        
        return this.data.dataFragments >= character.cost;
    }
    
    unlockCharacter(characterId) {
        const character = this.getCharacterData()[characterId];
        if (!character || !this.canUnlockCharacter(characterId)) return false;
        
        this.data.dataFragments -= character.cost;
        this.data.unlockedCharacters.push(characterId);
        this.saveProgress();
        return true;
    }
    
    getUnlockedCharacters() {
        const characters = this.getCharacterData();
        return this.data.unlockedCharacters.map(id => ({
            id,
            ...characters[id]
        }));
    }
    
    selectCharacter(characterId) {
        if (this.data.unlockedCharacters.includes(characterId)) {
            this.data.selectedCharacter = characterId;
            this.saveProgress();
            return true;
        }
        return false;
    }
    
    // Permanent upgrades
    canPurchaseUpgrade(upgradeType) {
        const costs = {
            healthBoost: [100, 200, 400, 800, 1600],
            speedBoost: [150, 300, 600, 1200, 2400],
            damageBoost: [200, 400, 800, 1600, 3200],
            expBoost: [120, 240, 480, 960, 1920],
            magnetRange: [300, 600, 1200],
            startingWeapon: [500, 1000, 2000],
            revival: [2000]
        };
        
        const currentLevel = this.data.permanentUpgrades[upgradeType];
        const maxLevel = costs[upgradeType].length;
        
        if (currentLevel >= maxLevel) return false;
        
        const cost = costs[upgradeType][currentLevel];
        return this.data.dataFragments >= cost;
    }
    
    purchaseUpgrade(upgradeType) {
        if (!this.canPurchaseUpgrade(upgradeType)) return false;
        
        const costs = {
            healthBoost: [100, 200, 400, 800, 1600],
            speedBoost: [150, 300, 600, 1200, 2400],
            damageBoost: [200, 400, 800, 1600, 3200],
            expBoost: [120, 240, 480, 960, 1920],
            magnetRange: [300, 600, 1200],
            startingWeapon: [500, 1000, 2000],
            revival: [2000]
        };
        
        const currentLevel = this.data.permanentUpgrades[upgradeType];
        const cost = costs[upgradeType][currentLevel];
        
        this.data.dataFragments -= cost;
        this.data.permanentUpgrades[upgradeType]++;
        this.saveProgress();
        return true;
    }
    
    // Session tracking
    startRun() {
        this.data.stats.totalRuns++;
        this.currentRunStats = {
            startTime: Date.now(),
            kills: 0,
            level: 1,
            maxLevel: 1,
            damageTaken: 0
        };
    }
    
    endRun(survivalTime, level, kills) {
        // Update statistics
        this.data.stats.totalKills += kills;
        this.data.stats.totalSurvivalTime += survivalTime;
        this.data.stats.bestSurvivalTime = Math.max(this.data.stats.bestSurvivalTime, survivalTime);
        this.data.stats.totalLevelsGained += level - 1;
        
        // Award data fragments based on performance
        const fragments = this.calculateFragmentReward(survivalTime, level, kills);
        this.data.dataFragments += fragments;
        this.data.totalDataFragments += fragments;
        
        // Check achievements
        this.checkAchievements();
        
        this.saveProgress();
        return fragments;
    }
    
    calculateFragmentReward(survivalTime, level, kills) {
        const baseReward = Math.floor(survivalTime / 10); // 1 per 10 seconds
        const levelBonus = (level - 1) * 5; // 5 per level gained
        const killBonus = Math.floor(kills / 10); // 1 per 10 kills
        
        return baseReward + levelBonus + killBonus;
    }
    
    checkAchievements() {
        for (const [id, achievement] of this.achievements) {
            if (this.data.stats.achievementsUnlocked.includes(id)) continue;
            
            if (achievement.condition(this.data.stats, this.currentRunStats)) {
                this.unlockAchievement(id);
            }
        }
    }
    
    unlockAchievement(id) {
        if (this.data.stats.achievementsUnlocked.includes(id)) return;
        
        const achievement = this.achievements.get(id);
        if (!achievement) return;
        
        this.data.stats.achievementsUnlocked.push(id);
        this.data.dataFragments += achievement.reward;
        this.data.totalDataFragments += achievement.reward;
        
        // Show achievement notification
        this.showAchievementNotification(achievement);
        
        this.saveProgress();
    }
    
    showAchievementNotification(achievement) {
        const notification = document.createElement('div');
        notification.className = 'achievement-notification';
        notification.innerHTML = `
            <div class="achievement-header">◆ ACHIEVEMENT UNLOCKED ◆</div>
            <div class="achievement-name">${achievement.name}</div>
            <div class="achievement-desc">${achievement.description}</div>
            <div class="achievement-reward">+${achievement.reward} Data Fragments</div>
        `;
        
        document.body.appendChild(notification);
        
        // Animate in
        setTimeout(() => notification.classList.add('show'), 100);
        
        // Remove after 4 seconds
        setTimeout(() => {
            notification.classList.add('hide');
            setTimeout(() => document.body.removeChild(notification), 500);
        }, 4000);
    }
    
    // Apply upgrades to game state
    applyUpgradesToPlayer(player, gameStats) {
        const upgrades = this.data.permanentUpgrades;
        const character = this.getCharacterData()[this.data.selectedCharacter];
        
        // Apply character modifiers
        if (character) {
            gameStats.maxHealth = Math.floor(gameStats.maxHealth * character.stats.healthMod);
            gameStats.health = gameStats.maxHealth;
            player.speed *= character.stats.speedMod;
            player.expMultiplier = character.stats.expMod;
            
            // Apply character abilities
            this.applyCharacterAbility(player, character);
        }
        
        // Apply permanent upgrades
        gameStats.maxHealth += upgrades.healthBoost * 10;
        gameStats.health = gameStats.maxHealth;
        player.speed *= 1 + (upgrades.speedBoost * 0.05);
        player.expMultiplier *= 1 + (upgrades.expBoost * 0.1);
        player.magnetRange = 1 + (upgrades.magnetRange * 0.2);
        
        if (upgrades.revival > 0) {
            player.hasRevival = true;
        }
    }
    
    applyCharacterAbility(player, character) {
        switch(character.name) {
            case 'The Guardian':
                player.damageReduction = (player.damageReduction || 0) + 0.25;
                break;
            case 'The Speedrunner':
                player.noMovementPenalty = true;
                break;
            case 'The Hacker':
                player.bonusPiercing = 1;
                break;
        }
    }
    
    // Getters
    getDataFragments() {
        return this.data.dataFragments;
    }
    
    getSelectedCharacter() {
        return this.data.selectedCharacter;
    }
    
    getStats() {
        return this.data.stats;
    }
    
    // Reset progress (for testing)
    resetProgress() {
        localStorage.removeItem('ascii_survivors_progress');
        this.data = this.getDefaultData();
    }
}

// Global instance
export const metaProgression = new MetaProgression(); 